import { FunnelIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';

import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@/components/core/Table';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { useRoleNames } from '../user-groups.queries';

interface SelectableRole {
    id: string;
    text: string;
    checked: boolean;
}

interface AddRolesPanelProps {
    open: boolean;
    roles: string[];
    onAdd: (roles: string[]) => void;
    onRemove: (role: string) => void;
}

const AddRolesPanel = ({
    open,
    roles,
    onAdd,
    onRemove,
}: AddRolesPanelProps) => {
    const { t } = useTranslation();
    const { data, isLoading } = useRoleNames({ enabled: open });
    const [rows, setRows] = useState<SelectableRole[]>([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            setSearch('');
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const nextRows = (data ?? [])
            .map((role) => {
                const alreadyAdded = roles.includes(role);
                return {
                    id: role,
                    text: role,
                    checked: alreadyAdded,
                };
            })
            .sort((left, right) => left.text.localeCompare(right.text));
        setRows(nextRows);
    }, [open, data, roles]);

    const visibleRows = useMemo(() => {
        const query = search.toLowerCase();
        if (!query) {
            return rows;
        }
        return rows.filter((row) => row.text.toLowerCase().includes(query));
    }, [rows, search]);

    if (!open) {
        return null;
    }

    return (
        <div className="tw-my-4 tw-rounded-md tw-border tw-border-solid tw-border-[#dee6ed] tw-p-4">
            <Input
                id="groupRoleFilter"
                name="groupRoleFilter"
                icon={FunnelIcon}
                value={search}
                placeholder={`${t('SEARCH')} ${t('USER_GROUP.ROLE')}..`}
                onChange={(event) => setSearch(event.target.value)}
            />
            <div className="tw-mt-4 tw-max-h-80 tw-overflow-auto tw-border border-table">
                <Table
                    id="select-group-role-table"
                    className="!tw-table tw-w-full !tw-max-h-none !tw-overflow-visible"
                >
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={2} className="tw-text-center">
                                    <Loader />
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {!isLoading && rows.length < 1 ? (
                            <TableRow>
                                <TableCell colSpan={2} className="tw-text-center tw-text-t-sm lg:tw-text-t-base tw-text-t-secondary">
                                    <span>{t('USER_MANAGEMENT.NO_USERS_AVAILABLE')}</span>
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {!isLoading && rows.length > 0 ? visibleRows.map((role) => (
                            <TableRow key={role.id}>
                                <TableCell className="tw-w-12">
                                    <div className="tw-flex tw-h-9 tw-items-center tw-justify-center">
                                        <input
                                            className="check-member tw-h-4 tw-w-4 lg:tw-h-5 lg:tw-w-5"
                                            type="checkbox"
                                            checked={role.checked}
                                            onChange={(event) => {
                                                const checked = event.target.checked;
                                                setRows((current) => current.map((row) => (
                                                    row.id === role.id ? { ...row, checked } : row
                                                )));
                                                if (checked) {
                                                    onAdd([role.id]);
                                                } else {
                                                    onRemove(role.id);
                                                }
                                            }}
                                        />
                                    </div>
                                </TableCell>
                                <TableCell className="!tw-pl-4">
                                    <span
                                        className={cn(
                                            'tw-inline-flex tw-h-4 tw-items-center tw-rounded-full',
                                            'tw-bg-fill-secondary tw-px-1.5 tw-text-t-xs tw-text-t-primary',
                                            'lg:tw-h-5 lg:tw-px-2 lg:tw-text-t-sm',
                                        )}
                                    >
                                        {role.text}
                                    </span>
                                </TableCell>
                            </TableRow>
                        )) : null}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export { AddRolesPanel };
